import { X } from 'lucide-react';
import { FC, useState, useEffect } from 'react';

type FilterInputProps<T> = {
    value: string;
    onChange: (filter: string) => void;
    filterFn?: (filter: string) => void; 
    options: T[];
    id: string;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    optionLabelKey?: keyof T;
    optionValueKey?: keyof T;
};

const FilterInput = <T extends Record<string, any>>({
    value,
    onChange,
    filterFn,  
    options,
    id,
    placeholder = 'Choose...',
    disabled = false,
    className = '',
    optionLabelKey,
    optionValueKey,
}: FilterInputProps<T>) => {
    const [filter, setFilter] = useState<string>(value);

    useEffect(() => {
        setFilter(value);
        if (filterFn) {
            filterFn(value);  
        }
    }, [value, filterFn]);

    const handleClear = () => {
        setFilter('');
        onChange('');
        filterFn?.(''); 
    };

    const getOptionValue = (option: T) => (optionValueKey ? String(option[optionValueKey]) : String(option));
    const getOptionLabel = (option: T) => (optionLabelKey ? String(option[optionLabelKey]) : String(option));

    return (
        <div className="relative flex-1 flex items-center gap-2">
            <select
                value={value || placeholder}
                onChange={(e) => {
                    setFilter(e.target.value);
                    onChange(e.target.value);
                    filterFn?.(e.target.value); 
                }}
                className={`form-select border-slate-200
                    ${filter ? `text-slate-900` : `text-slate-400`}
                    focus:border-custom-500 focus:outline-none disabled:border-slate-300
                    disabled:bg-slate-100 disabled:text-slate-500 ${className}`}
                id={id}
                disabled={disabled}
                aria-label={`Filter by ${id}`}
            >
                <option disabled value={placeholder}>
                    {placeholder}
                </option>
                {options?.map((option: T) => (
                    <option key={getOptionValue(option)} value={getOptionValue(option)} className="text-slate-900">
                        {getOptionLabel(option)}
                    </option>
                ))}
            </select>
            {filter && !disabled && (
                <button
                    type="button"
                    onClick={handleClear}
                    className="absolute ltr:right-8 rtl:left-8 top-1/2 transform
                    -translate-y-1/2 p-1 hover:bg-slate-100 rounded-full transition-colors"
                    aria-label="Clear filter"
                >
                    <X className="size-4 text-slate-500" />
                </button>
            )}
        </div>
    );
};

export default FilterInput;
